
import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  Document,
  DocumentAttributes,
  ExternalValidationResults,
  IDocumentObjectsInformation,
  IDocumentObjectsInformationObject,
  IExternalValidationParameter,
  IReplaceParameters,
  IValidationInfo,
  IExternalValidationFormatInfo,
  IExternalValidationWorkflowInfo,
  RequestedObject,
} from "../../../domain/document";
import {
  Patient,
} from "../../../domain/patient";
import {
  IConfiguration,
  IBehaviorDocumentEditorValidatorsConfiguration,
} from "../../../domain/configuration";
import {
  ExternalSystemGetObjectValuesResult,
  ExternalSystemValidationRequestResult,
} from "../../../domain/externalSystem";
import { ExternalSystemTranslator } from "../../../infrastructure/externalSystem/ver2/index";
import { FormatValidatableMixin } from "../../format/mixins";
import { DocumentInitializer } from "../editor/helpers";
import { AsyncUtility, BrowserUtility } from "../../../utilities";
import { DomainRegistry } from "../../../domain";
import { ModalPresentableMixin } from "../../system";
import { EditingDocument } from "../editor";
import { DocumentMerger, RequestedDocumentProcessor } from "../helpers";

@Component
export default class DocumentEditViewBaseMixin extends Mixins(FormatValidatableMixin, ModalPresentableMixin) {
  @Prop({ default: false })
  isEmbedded: boolean;

  @Prop({ default: false})
  shouldRestrictDocumentNote: boolean;

  @Prop()
  patientNo: string;

  @Prop({ default: false})
  shouldCloseOnRegistered: boolean;

  @Prop()
  defaultToolTabName: string;

  @Prop()
  validateObjectValuesUrl: string;

  @Prop()
  getObjectValuesUrl: string;

  @Prop()
  shouldIndicatePrintButtonForcibly: boolean;

  @Prop()
  registeredMessage: string;

  @Prop()
  ignoreFixKey: string;

  @Prop()
  isReadRestrictionAlreadyHandled: boolean;

  data(): any {
    return {
      document: null as Document,
      isEditing: true,
      isFormatReady: false,
      isDocumentInitialized: false,
      isLoading: false,
      isWindowClosing: false,
      translator: new ExternalSystemTranslator(),
      isValidating: false,
    };
  }
  document: Document;
  isEditing: boolean;
  isFormatReady: boolean;
  isDocumentInitialized: boolean;
  isLoading: boolean;
  isWindowClosing: boolean;
  translator: ExternalSystemTranslator;
  isValidating: boolean;

  async created() {
    if (this.validationUrls && this.validationUrls.length > 0) {
      for (const url of this.validationUrls) {
        await this.validateExternalSystemUrl(url);
      }
    }
    if (this.validateObjectValuesUrl) {
      await this.validateExternalSystemUrl(this.validateObjectValuesUrl);
    }
  }

  async createDocument(name: string, create: () => Promise<Document>, shouldShowLoading: boolean = true): Promise<Document> {
    try {
      if (shouldShowLoading) {
        this.isLoading = true;
      }
      this.$logger.info(name + "を取得します。");
      const result = await create();
      return result;
    } finally {
      if (shouldShowLoading) {
        this.isLoading = false;
      }
    }
  }

  async initializeDocument(name: string, document: Document, initialize: (i: DocumentInitializer) => Promise<void>, shouldShowLoading: boolean = true): Promise<void> {
    try {
      if (shouldShowLoading) {
        this.isLoading = true;
      }
      this.$logger.info(name + "を初期化します。");
      const initializer = new DocumentInitializer(document);
      await initialize(initializer);
      if (!initializer.isExternalScriptCompleted) {
        this.$error("エラー", "外部スクリプト実行中にエラーが発生しました。");
      }
    } finally {
      if (shouldShowLoading) {
        this.isLoading = false;
      }
    }
  }

  onFormatReady(): void {
    this.isFormatReady = true;
  }

  onDocumentInitialized(): void {
    this.isDocumentInitialized = true;
  }

  get validator(): IBehaviorDocumentEditorValidatorsConfiguration {
    return this.$configuration.behavior.document.editor.validator;
  }

  get validationUrls(): string[] {
    return this.$configuration.behavior.document.editor.validator.urls;
  }

  get hasValidationUrl(): boolean {
    return (this.validationUrls && this.validationUrls.length > 0)
        || !!this.validateObjectValuesUrl;
  }

  get injectorUrls(): string[] {
    return this.$configuration.behavior.document.editor.injector.urls;
  }

  get objectValuesInjectorUrls(): string[] {
    const urls = this.injectorUrls && this.injectorUrls.length > 0
      ? this.injectorUrls.concat(this.getObjectValuesUrl)
      : [this.getObjectValuesUrl];
    return urls.filter(u => !!u);
  }

  get isLoaded(): boolean {
    return !!this.document;
  }

  get attributes(): DocumentAttributes {
    return this.readDocumentPropertyOrDefault(s => s.attributes, null);
  }

  set attributes(source: DocumentAttributes) {
    this.writeDocumentPropertyWhenValid(s => s.attributes = source);
  }

  get patient(): Patient {
    return this.readDocumentPropertyOrDefault(s => s.attributes.patient, null);
  }

  set patient(source: Patient) {
    this.writeDocumentPropertyWhenValid(s => s.attributes.patient = source);
  }

  get additionalHtmlContent(): string {
    return !!this.patient
      ? `<div style="padding-left: 1em; font-size: 150%; font-weight: bold">患者番号：` + this.patient.id + `</div>` +
        `<div style="padding-left: 1em; font-size: 150%; font-weight: bold">患者氏名：` + this.patient.name + `</div>`
      : null;
  }

  private readDocumentPropertyOrDefault<T>(read: (s: Document) => T, defaultValue: T): T {
    return this.document
      ? read(this.document)
      : defaultValue;
  }

  private writeDocumentPropertyWhenValid(write: (s: Document) => void): void {
    if (!this.document) {
      return;
    }
    const clone = this.document.clone();
    write(clone);
    clone.freezeItems();
    this.document = clone;
  }

  mounted() {
    const config = this.$store.getters.configuration as IConfiguration;
    if (config.behavior.document.editor.shouldConfirmToLeavePage) {
      // ページ遷移時に確認ダイアログを表示する.(文言は反映されないブラウザあり.)
      window.onbeforeunload = () => {
        if (!this.document) { return; }
        console.warn("編集中にページ遷移しようとしてるようです。");
        return config.behavior.document.editor.confirmMessageToLeavePage;
      };
      this.$registerCloseConfirmation(this.closeConfirmation);
    }
  }

  private async closeConfirmation(): Promise<boolean> {
    try {
      if (!this.document) { return true; }
      this.beginModal();
      const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
        "確認",
        this.splitByReturnCode(this.$configuration.behavior.document.editor.confirmMessageToCloseWindow),
        this.$configuration.behavior.document.editor.closeWindowConfirmation.buttons.confirmation.name,
        this.$configuration.behavior.document.editor.closeWindowConfirmation.buttons.cancellation.name,
        this.additionalHtmlContent
      );
      return (index === 0);
    } catch (e) {
      return false;
    } finally {
      this.endModal();
    }
  }

  private splitByReturnCode(text: string): string[] {
    if (!text) { return [""]; }
    return text.split(/\r\n|\n/);
  }

  async finishEditing(id: number, destinationRoute?: any, isTemporary?: boolean, archiveId?: number, isWindowClosing?: boolean): Promise<void> {
    this.isEditing = false;
    this.notifyRegisteredMessage();
    if (isWindowClosing || await this.closeWindowIfNeeded(isTemporary)) { return; }
    // nullが明示的に指定された場合は遷移しない.undefinedの場合は通常の遷移.
    if (destinationRoute !== null) {
      const route = destinationRoute
        ? destinationRoute
        : {
          path: "ShowDocument",
          query: {
            patientNo: this.patient.id,
            documentId: id.toString(),
            shouldNotifyRegistered: true.toString(),
            isTemporary: isTemporary.toString(),
            isReadRestrictionAlreadyHandled: true.toString(),
          }
        };
      this.$router.replace(route);
    }
  }

  notifyRegisteredMessage(): void {
    this.$logger.info("文書登録完了通知開始.");
    if (!this.registeredMessage) {
      this.$logger.info("registeredMessageが指定されてないので通知しません.");
      return;
    }
    try {
      const message = this.createPostMessage("registeredMessage", this.registeredMessage);
      if (!!window.opener) {
        this.$logger.info("openerへ通知します.");
        console.log(message);
        (window.opener as Window).postMessage(message, "*");
      } else if (window.parent !== window) {
        this.$logger.info("parentへ通知します.");
        console.log(message);
        (window.parent as Window).postMessage(message, "*");
      }
    } catch (e) {
      this.$logger.error(e);
    } finally {
      this.$logger.info("文書登録完了通知終了.");
    }
  }

  createPostMessage(key: string, value: any = null): string {
    const message = {
      key,
      value,
    };
    return JSON.stringify(message);    
  }

  async closeWindowIfNeeded(isTemporary: boolean): Promise<boolean> {
    const beginTimeAsMsec = (new Date()).getTime();
    const configuration = this.$configuration.behavior.document.editor.registerer.instantCloser;
    if (!this.shouldCloseOnRegistered && !configuration.isEnable) { return false; }
    // 参照画面を表示しない設定の場合はここで登録完了通知を出す
    this.isWindowClosing = true;
    const action = isTemporary ? "一時保存" : "登録";
    const additionalMessage = isTemporary ? this.$configuration.behavior.document.editor.notifications.registerAsTemporary.message : "";
    this.$successAsHTML(
      action + "完了",
      "<div>文書を" + action + "しました。</div><div>" + additionalMessage + "</div>"
    );
    // 追加の通知を行う設定の場合は通知を出す.
    if (configuration.notification.isEnable) {
      await AsyncUtility.sleep(configuration.notification.delayAsMsec); // IEで直前の$success()と被ってしまう事があるので待つ.
      this.$info("画面自動クローズ", configuration.notification.message.replace("$WAITTIMEASSEC$", Math.round(configuration.waitTimeAsMsec / 1000).toString()));
    }
    // 設定されてる時間待つ.
    const elapsedTimeAsMsec = (new Date()).getTime() - beginTimeAsMsec;
    if (elapsedTimeAsMsec < configuration.waitTimeAsMsec) {
      await AsyncUtility.sleep(configuration.waitTimeAsMsec);
    }
    this.$closeWindow(true);
    return true;
  }

  async cancelEditDocument(source: Document): Promise<void> {
    if (!source || !source.editToken) { return; }
    if (!this.$configuration.behavior.document.editor.conflict.editTokenManagement.isEnable) { return; }
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      await repository.cancelEditDocument(source.editToken.token);
    } catch (ex) {
      throw ex;
    }
  }

  async deleteDocumentEditTokens(editTokens: string[]): Promise<void> {
    if (!editTokens || editTokens.length === 0) { return; }
    if (!this.$configuration.behavior.document.editor.conflict.editTokenManagement.isEnable) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.deleteDocumentEditTokens(editTokens);
  }

  async redirectUneditableDocument(source: Document, path?: string): Promise<void> {
    await this.cancelEditDocument(source);
    const route = {
      path: path ? path : "ShowDocument",
      query: {
        patientNo: this.patient.id,
        documentId: source.documentId.toString(),
        shouldNotifyRegistered: false.toString(),
        isTemporary: source.attributes.isTemporary.toString(),
        notification: "DocuMaker文書でない文書は編集できません。",
      }
    };
    this.$router.replace(route);
  }

  private toExternalValidationParameter(document: Document, editingObjects: IDocumentObjectsInformation, workflowStatusCode?: number): IExternalValidationParameter {
    return {
      replaceParameters: this.toReplaceParameters(document),
      validationInfo: this.toValidationInfo(document, editingObjects, workflowStatusCode),
    };
  }

  private toReplaceParameters(document: Document): IReplaceParameters {
    if (!document || !document.attributes || !document.attributes.format) { return null; }
    return {
      masterCode: document.attributes.format.masterCode,
      formatOriginalId: document.attributes.format.originalId,
      patientId: document.attributes.patient.id,
      userId: this.$store.getters.user.id,
    };
  }

  private toValidationInfo(document: Document, editingObjects: IDocumentObjectsInformation, workflowStatusCode?: number): IValidationInfo {
    return {
      format: this.toExternalValidationFormatInfo(document),
      workflow: this.toExternalValidationWorkflowInfo(workflowStatusCode),
      objects: this.toExternalValidateObjectValues(editingObjects),
    };
  }

  private toExternalValidationFormatInfo(document: Document): IExternalValidationFormatInfo {
    if (!document || !document.attributes || !document.attributes.format) { return null; }
    return {
      id: document.attributes.format.id,
      name: document.attributes.format.name,
      originalId: document.attributes.format.originalId,
      masterCode: document.attributes.format.masterCode,
    };
  }

  private toExternalValidationWorkflowInfo(workflowStatusCode?: number): IExternalValidationWorkflowInfo {
    if (workflowStatusCode === undefined || workflowStatusCode === null) { return null; }
    return {
      code: workflowStatusCode,
    };
  }

  // IDocumentObjectsInformationSectionと型的には変わらないので今回はそのまま使う.
  private toExternalValidateObjectValues(editingObjects: IDocumentObjectsInformation): IDocumentObjectsInformationObject[] {
    return Enumerable.from(editingObjects ? editingObjects.sections : [])
      .aggregate([] as IDocumentObjectsInformationObject[], (p, s) => p.concat(s.objects));
  }

  async validateWithExternalValidationUrl(workflowStatusCode?: number): Promise<ExternalValidationResults> {
    try {
      this.isValidating = true;
      let requestedObjects: RequestedObject[] = [];
      if (this.validationUrls && this.validationUrls.length > 0) {
        for (const url of this.validationUrls) {
          if (!await this.validateExternalSystemUrl(url)) { throw new Error("許可されていない外部システムのURLが指定されています。"); }
          const configurationUrlValidationResult = await this.validateByUrl(url, workflowStatusCode);
          if (configurationUrlValidationResult && configurationUrlValidationResult.alerts && configurationUrlValidationResult.alerts.length > 0) {
            return configurationUrlValidationResult;
          }
          if (configurationUrlValidationResult && configurationUrlValidationResult.objects && configurationUrlValidationResult.objects.length > 0) {
            requestedObjects = requestedObjects.concat(configurationUrlValidationResult.objects);
          }
        }
      }
      if (!this.validateObjectValuesUrl) { return { alerts: [], objects: requestedObjects }; }
      if (!await this.validateExternalSystemUrl(this.validateObjectValuesUrl)) { throw new Error("許可されていない外部システムのURLが指定されています。"); }

      const result = await this.validateByUrl(this.validateObjectValuesUrl, workflowStatusCode);
      return (result && (!result.alerts || result.alerts.length === 0) && (requestedObjects.length > 0))
        ? { alerts: [], objects: requestedObjects.concat(result.objects) }
        : result;
    } finally {
      this.isValidating = false;
    }
  }

  async validateByUrl(url: string, workflowStatusCode?: number): Promise<ExternalValidationResults> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      const service = DomainRegistry.externalSystem.getExternalSystemService();
      const result = await service.postData<IExternalValidationParameter, ExternalSystemValidationRequestResult>(
        url,
        this.toExternalValidationParameter(this.document, await repository.getEditingDocumentObjects(this.document), workflowStatusCode)
      );
      const validationResult = this.translator.toExternalSystemValidationRequestResult(result);
      if (!validationResult || (validationResult && !validationResult.isOk)) {
        const errorMessage = validationResult && validationResult.message
          ? validationResult.message
          : "外部システムの警告処理中にエラーが発生しました。";
        // 404エラー等のエラーメッセージは通知に出さずにコンソールに出す
        this.$error("外部システムオブジェクト値検証失敗", "外部システムの警告処理中にエラーが発生しました。", errorMessage);
        throw new Error(errorMessage);
      }
      if (validationResult && validationResult.result && !validationResult.result.isOk) {
        const resultMessage = validationResult.result.message
          ? validationResult.result.message
          : "外部システムの警告処理中にエラーが発生しました。";
        // isOk=falseのエラーメッセージは通知に出す
        this.$error("外部システムオブジェクト値検証失敗", resultMessage, resultMessage);
        throw new Error(resultMessage);
      }
      return validationResult.result;
    } catch (ex) {
      throw ex;
    }
  }

  async applyExternalRequestObjectValues(document: EditingDocument, urls: string[], processor: RequestedDocumentProcessor): Promise<void> {
    for (const url of urls) {
      if (!await this.validateExternalSystemUrl(url)) { throw new Error("許可されていない外部システムのURLが指定されています。"); }
      await this.applyRequestedValues(document, url, processor);
    }
  }

  private async applyRequestedValues(document: EditingDocument, url: string, processor: RequestedDocumentProcessor): Promise<void> {
    try {
      const service = DomainRegistry.externalSystem.getExternalSystemService();
      const parameters = this.toReplaceParameters(document.source);
      const result = await service.getData<ExternalSystemGetObjectValuesResult>(url, parameters);
      const requestResult = this.translator.toExternalSystemGetObjectValuesResult(result);
      if (!requestResult || (requestResult && !requestResult.isOk)) {
        const errorMessage = requestResult && requestResult.message
          ? requestResult.message
          : "外部システムのデータ取得中にエラーが発生しました。";
        // 404エラー等のエラーメッセージは通知に出さずにコンソールに出す
        this.$error("外部システムのデータ取得失敗", "外部システムのデータ取得中にエラーが発生しました。", errorMessage);
        return;
      }
      if (requestResult && requestResult.result && !requestResult.result.isOk) {
        const resultMessage = requestResult.result.message
          ? requestResult.result.message
          : "外部システムのデータ取得中にエラーが発生しました。";
        // isOk=falseのエラーメッセージは通知に出す
        this.$error("外部システムのデータ取得失敗", resultMessage, resultMessage);
        return;
      }
      if (!requestResult.result.objects || (requestResult.result.objects && requestResult.result.objects.length === 0)) {
        return;
      }
      await processor.applyRequestedValues(document, requestResult.result.objects);
    } catch (e) {
      this.$error(
        "外部システムのデータ取得失敗",
        "外部システムのデータ取得中にエラーが発生しました。",
        e,
      );
    }
  }

  private async validateExternalSystemUrl(url: string): Promise<boolean> {
    try {
      const service = DomainRegistry.externalSystem.getExternalSystemService();
      const result = await service.validateUrl(url);
      if (result) { return result; }

      this.$errorAsHTML(
        "外部システムオブジェクト値検証URL不正",
        "<div>許可されていない外部システムのURLが指定されています。</div><div style='word-break: break-all;'>" + url + "</div>",
        url
      );
      return false;
    } catch (ex) {
      this.$error(
        "外部システムオブジェクト値検証URL許可判定失敗",
        "外部システムオブジェクト値検証URLの許可判定に失敗しました。",
        url
      );
      return false;
    }
  }

  public shouldChainDocuments(source: Document): boolean {
    return (!source.documentId || source.documentId && source.edition === 0)
        && source.chainableFormatOriginalIds
        && (0 < source.chainableFormatOriginalIds.length);
  }

  public hasChainedDocuments(source: Document): boolean {
    return source.documentId
        && source.chainedDocuments
        && (0 < source.chainedDocuments.length);
  }

  public shouldConfirmRegisterRevisedChainDocuments(source: Document): boolean {
    return this.$configuration.behavior.document.editor.shouldConfirmRegisterRevisedChainDocuments
      && !this.shouldChainDocuments(source)
      && this.hasChainedDocuments(source);
  }

  async mergeDocument(document: Document, valueSourceDocument: Document, shouldIgnoreObjectPostability: boolean = false): Promise<Document> {
    const merger = new DocumentMerger(document);
    const isByName = !merger.isSameFormat(valueSourceDocument);
    merger.mergeDocument(valueSourceDocument, isByName, shouldIgnoreObjectPostability);
    await merger.copyImagesFromDocumentAsync();
    if (BrowserUtility.isChromiumBrowser()) {
      if (merger.source.externalScriptRunner.hasRequestedScripts) {
        const config = this.$store.getters.configuration as IConfiguration;
        if (!config.behavior.localhost.internalScript.isEnable) {
          // ChromeはUrlSchemeを叩く間隔が短いと対象のEXEを起動できない現象が発生する。
          // 3.5秒程度の間隔を空けると起動できる模様なので、無理やり間隔を空けている。
          this.$logger.debug("ChromeのUrlScheme対策でsleepします。");
          await AsyncUtility.sleep(4000);
          this.$logger.debug("ChromeのUrlScheme対策でsleepしました。");
        }
      }
    }
    await merger.executeScriptInDocumentAtCalculateAsync(this.$configuration.behavior.document.editor.externalScript.shouldExecuteAsStackedRequest);
    const isSuccess = await merger.executeStackedScriptsAsyncWithResult();
    if (!isSuccess) {
      this.$error("外部スクリプト", "外部スクリプトの実行中にエラーが発生しています。");
    }
    return merger.sourceAsDocument;
  }

  public async toUnrevisedChainedDocument(source: Document, revisedChainDocuments: Document[]): Promise<Document> {
    return Enumerable.from(revisedChainDocuments).any(d => d.documentId === source.documentId)
      ? null
      : source;
  }

  public async confirmRegisterRevisedChainDocuments(revisedChainDocuments: Document[]): Promise<boolean> {
    return (await this.$commonDialogs.confirmation.confirmRegisterCancel(null, [
      "更新後に直接改版された文書があります。(" + revisedChainDocuments.length + "文書)",
      "改版されている文書にも入力内容を反映しますか？",
      "(直接改版された際に更新された内容を上書きしてしまう可能性があります。)",
    ]) === 0);
  }

  public getRevisedChainDocuments(source: Document): Document[] {
    return (source.chainDocuments && (0 < source.chainDocuments.length))
      ? Enumerable.from(source.chainDocuments).selectMany(d => (Enumerable.from(source.chainedDocuments).any(i => (i.id === d.documentId) && (i.edition !== d.edition)) ? [ d ] : []).concat(this.getRevisedChainDocuments(d))).toArray()
      : [];
  }

  async validateSingleEntry(patientCode: string, formatOriginalId: number, shouldShowNotification: boolean): Promise<void> {
    if (!this.$configuration.behavior.document.editor.singleEntry.isEnable) { return; }
    const repo = DomainRegistry.document.getDocumentRepository();
    const info = await repo.getSingleEntryCheckInformation(patientCode, formatOriginalId);
    if (info.editInformation || info.registeredDocumentSummary) {
      const message = "重複記載が禁止されたフォーマットです。";
      const reason = info.editInformation
        ? "別の文書が記載中のため、処理続行できません。"
        : "別の文書が存在しているため、処理続行できません。";
      if (shouldShowNotification) {
        this.$error("重複記載", message + reason);
      }
      throw new Error(message + reason);
    }
  }

  async isSingleEntryFormat(formatOriginalId: number): Promise<boolean> {
    if (!this.$configuration.behavior.document.editor.singleEntry.isEnable) { return false; }
    const repo = DomainRegistry.format.getFormatRepository();
    return repo.isSingleEntry(formatOriginalId);
  }
}
